import { useEffect, useMemo, useRef } from "react";

import { Box, Flex, SimpleGrid, Button } from "@mantine/core";
import { UseFormReturnType } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import { DECIMAL_PROPS, NumberInput, Select, TextInput, YesNoRadioButton } from "components";
import { IRootState } from "config/redux";
import { setDialogError } from "config/redux/slice";
import { ServiceConstants } from "constant";
import { CreditService } from "services";
import { formClasses } from "styles";
import {
  BankCreditOptionsResponse,
  BankCreditRequestForm,
  BankCreditResponse,
  CreditResponse,
  PageResponse,
  ProductBankNow,
} from "types";
import { AppUtils } from "utils";

import SectionTitle from "../../components/section-title";

const paymentPeriodOptions = [12, 18, 24, 30, 36, 42, 48, 54, 60, 66, 72, 78, 84];

interface BankCreditRequestRequestProps {
  credit: BankCreditResponse;
  form: UseFormReturnType<BankCreditRequestForm>;
  options: BankCreditOptionsResponse;
  visibleFields: string[];
  readOnly?: boolean;
}

export const BankCreditRequestRequest = ({
  credit,
  form,
  options,
  visibleFields,
  readOnly = false,
}: BankCreditRequestRequestProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isUpdatingFromPreviousCredit = useRef(false);
  const isInitialMountPaymentPeriod = useRef(true);
  const isInitialMountProductBn = useRef(true);

  const userId = credit.user.id;
  const previousCreditServiceObject = useSelector(
    (state: IRootState) => state.service.object[ServiceConstants.REQUEST_CREDIT_PREVIOUS_ACTIVE_ID],
  );
  const isLoadingPreviousCredit =
    previousCreditServiceObject?.status === ServiceConstants.REQUEST_PENDING;

  const formValues = form.getValues();
  const mortgage = formValues?.mortgage;
  const rent = formValues?.rent;
  const livingFrom = formValues?.livingFrom;
  const bank = formValues?.bank;
  const birthDate = formValues?.birthDate;
  const employments = formValues?.employments;
  const incomes = formValues?.incomes;
  const productBn = formValues?.productBn;

  const professionalSituationArray = useMemo(() => {
    return employments?.map((e) => e.professionalSituation) ?? [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employments?.map((e) => e.professionalSituation).join("|")]);

  const incomeTypesArray = useMemo(() => {
    return incomes?.map((i) => i.incomeType) ?? [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incomes?.map((i) => i.incomeType).join("|")]);

  const allowedPaymentPeriods = useMemo(() => {
    const hasInvalidityPension = professionalSituationArray?.some(
      (s) => s === "DISABILITY_PENSION",
    );
    const hasSUVAAdditionalIncome = incomeTypesArray?.some(
      (income) => income === "PENSIONSKASSE_SUVA",
    );
    const hasAgePension = professionalSituationArray?.some((s) => s === "PENSION");
    const hasAgency = professionalSituationArray?.some((s) => s === "AGENCY");

    // For Agency (Temporär) in Bank Now: maximum 36 months
    if (hasAgency && bank === "BANK_NOW") {
      return paymentPeriodOptions.filter((period) => period <= 36);
    }

    if (hasInvalidityPension) {
      if (hasSUVAAdditionalIncome) {
        return [36, 48];
      }
      return [36];
    }

    if (!birthDate) {
      return paymentPeriodOptions;
    }

    // For AHV (old-age pension): payment period can last at most until 70 years of age
    if (hasAgePension) {
      const dateAt70 = moment(birthDate).add(70, "years");
      const maxMonths = dateAt70.diff(moment(), "months");
      return paymentPeriodOptions.filter((period) => period <= maxMonths);
    }

    // For all other users: payment period must not exceed 65 years of age
    const dateAt65 = moment(birthDate).add(65, "years");
    const maxMonths = dateAt65.diff(moment(), "months");

    return paymentPeriodOptions.filter((period) => period <= maxMonths);
  }, [birthDate, professionalSituationArray, incomeTypesArray, bank]);

  useEffect(() => {
    AppUtils.resolveServiceObject(
      previousCreditServiceObject,
      ServiceConstants.REQUEST_CREDIT_PREVIOUS_ACTIVE_ID,
      (data: PageResponse) => {
        const credits = data.content as CreditResponse[];

        if (credits && credits.length > 0) {
          const previousCredit = credits?.[0];
          const updatedValues: Partial<BankCreditRequestForm> = {};

          if (previousCredit?.grantedCreditAmount) {
            updatedValues.existingCreditAmount = previousCredit?.grantedCreditAmount;
          }
          if (previousCredit?.grantedInstallmentAmount) {
            updatedValues.existingMonthlyInstallment = previousCredit?.grantedInstallmentAmount;
          }
          if (previousCredit?.bank) {
            updatedValues.bank = previousCredit?.bank;
          }

          isUpdatingFromPreviousCredit.current = true;
          form.setValues(updatedValues);
          setTimeout(() => {
            isUpdatingFromPreviousCredit.current = false;
          }, 100);

          notifications.show({
            title: t("bankCredit.internalContractData.success.title"),
            message: t("bankCredit.internalContractData.success.message"),
            color: "green",
          });
        } else {
          dispatch(
            setDialogError({
              status: 404,
              data: {
                code: "NO_PREVIOUS_CREDIT",
                message: t("bankCredit.internalContractData.error.title"),
                description: t("bankCredit.internalContractData.error.message"),
              },
            }),
          );
        }
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previousCreditServiceObject]);

  useEffect(() => {
    // Skip the effect on initial mount to avoid interfering with form initialization
    if (isInitialMountProductBn.current) {
      isInitialMountProductBn.current = false;
      return;
    }

    if (isUpdatingFromPreviousCredit.current) {
      return;
    }

    if (!readOnly && visibleFields.includes("productBn")) {
      let calculatedProduct: ProductBankNow = "CLASSIC";

      // Only set to CASA if owns mortgage (not renting) and living there for more than 6 months
      if (mortgage && !rent && livingFrom) {
        const monthsDiff = moment().diff(moment(livingFrom), "months");
        if (monthsDiff > 6) {
          calculatedProduct = "CASA";
        }
      }

      if (calculatedProduct !== productBn) {
        form.setFieldValue("productBn", calculatedProduct);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mortgage, rent, livingFrom, productBn, readOnly, visibleFields]);

  useEffect(() => {
    if (isInitialMountPaymentPeriod.current) {
      isInitialMountPaymentPeriod.current = false;
      return;
    }

    const currentPaymentPeriod = form.getValues().paymentPeriod;
    if (currentPaymentPeriod && !allowedPaymentPeriods.includes(currentPaymentPeriod)) {
      form.setFieldValue("paymentPeriod", undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowedPaymentPeriods]);

  const getActiveCreditDataFromInternalContract = () => {
    if (!userId) {
      dispatch(
        setDialogError({
          status: 400,
          data: {
            code: "NO_USER_ID",
            message: t("bankCredit.internalContractData.error.title"),
            description: t("bankCredit.internalContractData.error.noUser"),
          },
        }),
      );
      return;
    }

    CreditService.creditPreviousRequests(
      String(userId),
      ServiceConstants.REQUEST_CREDIT_PREVIOUS_ACTIVE_ID,
      { page: 0, pageSize: 1, onlyActive: true },
    );
  };

  return (
    <Flex direction="column" p="md" gap="md">
      <Box>
        <SimpleGrid cols={4}>
          <NumberInput
            label={t("credit.creditAmount.label")}
            key={form.key("creditAmount")}
            {...form.getInputProps("creditAmount")}
            min={options["creditAmount"]?.min}
            max={options["creditAmount"]?.max}
            required={true}
            disabled={readOnly}
          />
          <Select
            label={t("credit.paymentPeriod.label")}
            data={allowedPaymentPeriods.map((n) => ({
              value: String(n),
              label: String(n),
            }))}
            key={form.key("paymentPeriod")}
            defaultValue={
              form.getValues().paymentPeriod ? String(form.getValues().paymentPeriod) : null
            }
            onChange={(value) => form.setFieldValue("paymentPeriod", Number(value))}
            error={form.errors.paymentPeriod}
            clearable={true}
            required={true}
            disabled={readOnly}
          />
          <YesNoRadioButton
            label={t("credit.creditInsurance.label")}
            name={"creditInsurance"}
            form={form}
            required={true}
            disabled={readOnly}
          />
          {visibleFields.includes("productBn") && (
            <Select
              label={t("credit.productBn.label")}
              data={[
                { value: "CASA", label: t("credit.productBn.options.CASA") },
                { value: "CLASSIC", label: t("credit.productBn.options.CLASSIC") },
              ]}
              key={form.key("productBn")}
              {...form.getInputProps("productBn")}
              disabled
            />
          )}
        </SimpleGrid>
        <SimpleGrid cols={4} mt={"sm"}>
          <Select
            label={t("credit.creditType.label")}
            data={options["creditType"].map((el) => ({
              value: el,
              label: t(`credit.creditType.options.${el}`),
            }))}
            key={form.key("creditType")}
            {...form.getInputProps("creditType")}
            clearable={true}
            required={true}
            disabled={readOnly}
          />
          {visibleFields.includes("creditPurpose") && (
            <Select
              label={t("credit.creditPurpose.label")}
              data={options["creditPurpose"].map((el) => ({
                value: el,
                label: t(`credit.creditPurpose.options.${el}`),
              }))}
              key={form.key("creditPurpose")}
              {...form.getInputProps("creditPurpose")}
              clearable={true}
              required={true}
              disabled={readOnly}
            />
          )}
          {visibleFields.includes("creditPurposeNote") && (
            <TextInput
              label={t("credit.creditPurposeNote.label")}
              key={form.key("creditPurposeNote")}
              {...form.getInputProps("creditPurposeNote")}
              required={true}
              disabled={readOnly}
            />
          )}
        </SimpleGrid>
      </Box>

      {visibleFields.some((f) =>
        [
          "existingMonthlyInstallment",
          "existingCreditAmount",
          "bank",
          "bankName",
          "refinanceCreditCard",
          "refinanceLease",
        ].includes(f),
      ) && (
        <Box mt={"sm"}>
          <SimpleGrid cols={4}>
            {visibleFields.includes("existingCreditAmount") && (
              <Box>
                <NumberInput
                  label={t("bankCredit.existingCreditAmount.label")}
                  key={form.key("existingCreditAmount")}
                  {...form.getInputProps("existingCreditAmount")}
                  required={true}
                  disabled={readOnly}
                />
                {!readOnly && (
                  <Button
                    onClick={getActiveCreditDataFromInternalContract}
                    variant="subtle"
                    c="var(--mantine-color-green-7)"
                    td="underline"
                    size="compact-sm"
                    mt={4}
                    p={4}
                    fw={"normal"}
                    loading={isLoadingPreviousCredit}
                  >
                    {t("bankCredit.internalContractData.button")}
                  </Button>
                )}
              </Box>
            )}
            {visibleFields.includes("existingMonthlyInstallment") && (
              <NumberInput
                label={t("credit.existingMonthlyInstallment.label")}
                key={form.key("existingMonthlyInstallment")}
                min={options["existingMonthlyInstallment"]?.min}
                max={options["existingMonthlyInstallment"]?.max}
                {...form.getInputProps("existingMonthlyInstallment")}
                required={true}
                disabled={readOnly}
                {...DECIMAL_PROPS}
              />
            )}
            {visibleFields.includes("bank") && (
              <Select
                label={t("credit.bank.label")}
                data={options["bank"].map((el) => ({
                  value: el,
                  label: t(`credit.bank.options.${el}`),
                }))}
                key={form.key("bank")}
                {...form.getInputProps("bank")}
                clearable={true}
                required={true}
                disabled={readOnly}
              />
            )}
            {visibleFields.includes("bankName") && (
              <TextInput
                label={t("credit.bankName.label")}
                key={form.key("bankName")}
                {...form.getInputProps("bankName")}
                required={true}
                disabled={readOnly}
              />
            )}
            {visibleFields.includes("refinanceLease") && (
              <YesNoRadioButton
                label={t("credit.refinanceLease.label")}
                name={"refinanceLease"}
                form={form}
                required={true}
                disabled={readOnly}
              />
            )}
            {visibleFields.includes("refinanceCreditCard") && (
              <YesNoRadioButton
                label={t("credit.refinanceCreditCard.label")}
                name={"refinanceCreditCard"}
                form={form}
                required={true}
                disabled={readOnly}
              />
            )}
          </SimpleGrid>
        </Box>
      )}

      {visibleFields.some((f) =>
        [
          "existingLeaseAmount",
          "existingLeaseMonthlyInstallment",
          "existingLeaseResidualValue",
        ].includes(f),
      ) && (
        <Box className={formClasses.sectionBorder} mt={"sm"}>
          <SectionTitle label={t("bankCredit.existingLease.sectionTitle")} isBankCredit />

          <SimpleGrid cols={4}>
            {visibleFields.includes("existingLeaseAmount") && (
              <NumberInput
                label={t("bankCredit.existingLeaseAmount.label")}
                key={form.key("existingLeaseAmount")}
                {...form.getInputProps("existingLeaseAmount")}
                disabled={readOnly}
              />
            )}
            {visibleFields.includes("existingLeaseMonthlyInstallment") && (
              <NumberInput
                label={t("bankCredit.existingLeaseMonthlyInstallment.label")}
                key={form.key("existingLeaseMonthlyInstallment")}
                {...form.getInputProps("existingLeaseMonthlyInstallment")}
                disabled={readOnly}
              />
            )}
            {visibleFields.includes("existingLeaseResidualValue") && (
              <NumberInput
                label={t("bankCredit.existingLeaseResidualValue.label")}
                key={form.key("existingLeaseResidualValue")}
                {...form.getInputProps("existingLeaseResidualValue")}
                disabled={readOnly}
              />
            )}
          </SimpleGrid>
        </Box>
      )}

      <SimpleGrid cols={4} mt={"lg"}>
        <TextInput
          label={t("credit.comment.singular")}
          key={form.key("comment1")}
          {...form.getInputProps("comment1")}
          disabled={readOnly}
        />
      </SimpleGrid>
    </Flex>
  );
};
